/**
 * ===============================================================
 * orderPayment.js
 * ===============================================================
 *
 * Ce module centralise toute la logique liée :
 * - aux statuts de paiement
 * - aux messages utilisateur
 * - à la normalisation des montants (centimes vs CHF)
 *
 * Il est utilisé par :
 *
 *  - AccountView.jsx
 *  - OrderDetailsModal.jsx
 *  - (et potentiellement futur dashboard admin)
 *
 * OBJECTIF PRINCIPAL
 * ---------------------------------------------------------------
 * Garantir que les montants affichés soient TOUJOURS corrects
 * malgré les différentes sources possibles :
 *
 * 1️⃣ Base SQL
 *    total_ttc stocké en CHF décimal
 *    exemple : 5.00
 *
 * 2️⃣ API get_orders.php
 *    convertit total_ttc en CENTIMES
 *    exemple : 500
 *
 * 3️⃣ Items JSON
 *    peuvent contenir :
 *      - prix en CHF
 *      - prix en centimes
 *
 * Le rôle de normalizeOrderTotalCents() est donc de détecter
 * automatiquement le format et de renvoyer TOUJOURS :
 *
 * ➜ un montant en CENTIMES
 *
 * afin que formatCHF() fonctionne correctement.
 *
 * ===============================================================
 */

/**
 * ---------------------------------------------------------------
 * STATUTS DE COMMANDE PAYÉES
 * ---------------------------------------------------------------
 *
 * Ces statuts signifient que le paiement est confirmé
 * et que la commande peut être :
 *
 * - préparée
 * - remise
 * - livrée
 */
export const PAID_STATUSES = [
  "paid",
  "succeeded",
  "ready_for_pick",
  "ready_for_delivery",
  "delivered",
  "ready",
];

/**
 * ---------------------------------------------------------------
 * STATUTS EN ATTENTE DE PAIEMENT
 * ---------------------------------------------------------------
 *
 * Ces statuts signifient que la commande existe mais que
 * le paiement n'est PAS encore confirmé.
 *
 * IMPORTANT :
 * Dans ces cas la commande NE DOIT PAS être préparée.
 */
export const PENDING_PAYMENT_STATUSES = [
  "pending",
  "pending_payment",
  "payment_pending",
  "unpaid",
  "requires_payment_method",
  "requires_action",
  "requires_confirmation",
  "checkout_started",
  "created",
  "open",
  "processing",
];

/**
 * ---------------------------------------------------------------
 * STATUTS ANNULÉS
 * ---------------------------------------------------------------
 */
export const CANCELLED_STATUSES = ["cancelled", "canceled"];

/**
 * ---------------------------------------------------------------
 * NORMALISE UN STATUS
 * ---------------------------------------------------------------
 *
 * Convertit en string propre :
 * - trim
 * - lowercase
 */
const toStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

/**
 * ---------------------------------------------------------------
 * STATUS PAYÉ ?
 * ---------------------------------------------------------------
 */
export const isPaidStatus = (status) =>
  PAID_STATUSES.includes(toStatus(status));

/**
 * ---------------------------------------------------------------
 * STATUS NON PAYÉ ?
 * ---------------------------------------------------------------
 */
export const isUnpaidStatus = (status) =>
  PENDING_PAYMENT_STATUSES.includes(toStatus(status));

/**
 * ---------------------------------------------------------------
 * STATUS ANNULÉ ?
 * ---------------------------------------------------------------
 */
export const isCancelledStatus = (status) =>
  CANCELLED_STATUSES.includes(toStatus(status));

/**
 * ---------------------------------------------------------------
 * STATUS EN TRAITEMENT STRIPE
 * ---------------------------------------------------------------
 *
 * Cas où Stripe est encore en train de confirmer le paiement.
 */
export const isProcessingPaymentStatus = (status) => {
  const s = toStatus(status);
  return s === "processing" || s === "requires_confirmation";
};

/**
 * ===============================================================
 * LABEL HUMAIN DU STATUS
 * ===============================================================
 *
 * Convertit les statuts techniques en texte affichable
 * dans l'interface client.
 */
export const getOrderStatusLabel = (status) => {
  const s = toStatus(status);

  if (PAID_STATUSES.includes(s)) {
    if (s === "ready_for_pick") return "Prête au retrait";

    if (s === "ready_for_delivery") return "Prête en livraison";

    if (s === "delivered") return "Livrée";

    return "Payée";
  }

  if (s === "processing") {
    return "Paiement en cours de confirmation";
  }

  if (s === "requires_confirmation") {
    return "Confirmation du paiement en cours";
  }

  if (PENDING_PAYMENT_STATUSES.includes(s)) {
    if (s === "requires_action") return "Action de paiement requise";

    if (s === "requires_payment_method") return "Moyen de paiement requis";

    return "Paiement non confirmé";
  }

  if (CANCELLED_STATUSES.includes(s)) {
    return "Annulée";
  }

  return String(status || "—");
};

/**
 * ===============================================================
 * MESSAGE D'INFORMATION SUR LE PAIEMENT
 * ===============================================================
 *
 * Affiché dans les détails de commande.
 */
export const getOrderPaymentNotice = (status) => {
  const s = toStatus(status);

  if (isPaidStatus(s)) {
    return "Votre paiement a bien été confirmé.";
  }

  if (s === "processing") {
    return "Votre paiement est en cours de traitement.";
  }

  if (s === "requires_confirmation") {
    return "Confirmation finale du paiement en cours.";
  }

  if (s === "requires_action") {
    return "Une action est requise pour finaliser le paiement.";
  }

  if (isUnpaidStatus(s)) {
    return "Votre commande n'est pas encore payée.";
  }

  if (isCancelledStatus(s)) {
    return "Cette commande a été annulée.";
  }

  return "";
};

/**
 * ===============================================================
 * PARSE LES ITEMS D'UNE COMMANDE
 * ===============================================================
 *
 * Les items peuvent arriver sous plusieurs formats :
 *
 * 1️⃣ tableau JS
 * 2️⃣ string JSON
 *
 * Cette fonction garantit qu'on obtient toujours :
 *
 * ➜ un tableau
 */
const parseItemsArray = (items) => {
  if (!items) return [];

  if (Array.isArray(items)) return items;

  if (typeof items === "string") {
    try {
      const parsed = JSON.parse(items);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

/**
 * ===============================================================
 * SOMME DES ITEMS
 * ===============================================================
 *
 * Calcule le total brut à partir des lignes produits.
 *
 * Utile pour détecter si un total est :
 *
 * - en CHF
 * - en centimes
 */
const sumItemsRaw = (items) => {
  const safeItems = parseItemsArray(items);

  if (safeItems.length === 0) return 0;

  return safeItems.reduce((acc, item) => {
    const qty = Number(item?.qty ?? item?.quantite ?? item?.quantity ?? 1) || 1;

    const price = Number(
      item?.appliedPrice ?? item?.price ?? item?.prix ?? item?.unit_price ?? 0
    );

    if (!Number.isFinite(price) || price <= 0) return acc;

    return acc + price * qty;
  }, 0);
};

/**
 * ===============================================================
 * DETECTE UN MONTANT EN CENTIMES
 * ===============================================================
 */
const looksLikeCents = (value) => {
  return Number.isInteger(value);
};

/**
 * ===============================================================
 * DETECTE UN MONTANT EN CHF DECIMAL
 * ===============================================================
 */
const looksLikeDecimalChf = (value) => {
  return Number.isFinite(value) && !Number.isInteger(value);
};

/**
 * ===============================================================
 * NORMALISATION DU TOTAL COMMANDE
 * ===============================================================
 *
 * Fonction critique du système.
 *
 * Elle garantit que le total retourné est TOUJOURS
 * exprimé en centimes.
 *
 * Cas gérés :
 *
 * 1️⃣ total stocké en CHF décimal
 * 2️⃣ total déjà en centimes
 * 3️⃣ détection via les lignes produits
 */
export const normalizeOrderTotalCents = (rawTotal, items = []) => {
  const totalNum = Number(rawTotal);

  if (!Number.isFinite(totalNum) || totalNum <= 0) return 0;

  const safeItems = parseItemsArray(items);

  const itemsRaw = sumItemsRaw(safeItems);

  /**
   * ---------------------------------------------------------------
   * CAS 1
   * total = CHF décimal
   *
   * Exemple :
   * 5.00
   *
   * → conversion en centimes
   */
  if (looksLikeDecimalChf(totalNum)) {
    return Math.round(totalNum * 100);
  }

  /**
   * ---------------------------------------------------------------
   * CAS 2
   * total = entier
   *
   * On compare avec les items pour savoir si :
   *
   * - total est déjà en centimes
   * - total est en CHF
   */
  if (looksLikeCents(totalNum) && itemsRaw > 0) {
    const asCents = Math.round(totalNum);

    const asChfToCents = Math.round(totalNum * 100);

    const itemsAsCents = Math.round(itemsRaw);

    const diffIfTotalAlreadyCents = Math.abs(asCents - itemsAsCents);

    const diffIfTotalIsChf = Math.abs(asChfToCents - itemsAsCents);

    /**
     * Si la somme des items ressemble déjà
     * à des centimes → on garde tel quel
     */
    if (diffIfTotalAlreadyCents <= diffIfTotalIsChf) {
      return asCents;
    }

    /**
     * Sinon le total était probablement
     * en CHF → conversion
     */
    return asChfToCents;
  }

  /**
   * ---------------------------------------------------------------
   * CAS 3
   * entier sans items
   *
   * Dans Marché Deluxe :
   *
   * get_orders.php renvoie les totaux
   * déjà en centimes.
   */
  return Math.round(totalNum);
};
